import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Lock, Loader2, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('auth.invalidResetLink') || 'Invalid or missing reset link');
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('auth.passwordMinLength') || 'Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsMustMatch') || 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" data-testid="reset-pw-logo" />
          </Link>
        </div>
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold mb-2" data-testid="reset-pw-title">
              {t('auth.newPassword') || 'Set New Password'}
            </h1>
            <p className="text-white/50 text-sm">
              {t('auth.enterNewPassword') || 'Enter your new password below'}
            </p>
          </div>

          {success ? (
            <div className="text-center py-6" data-testid="reset-pw-success">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-lg font-semibold mb-2">{t('auth.passwordUpdated') || 'Password Updated!'}</h2>
              <p className="text-white/50 text-sm mb-4">
                {t('auth.redirectingToLogin') || 'Redirecting to login...'}
              </p>
              <Link to="/login" className="text-[#E8720C] hover:underline text-sm font-medium">
                {t('auth.goToLogin') || 'Go to Login'}
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center py-6" data-testid="reset-pw-invalid">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-lg font-semibold mb-2 text-red-400">{t('auth.invalidLink') || 'Invalid Link'}</h2>
              <p className="text-white/50 text-sm mb-4">
                {t('auth.invalidLinkMessage') || 'This reset link is invalid or has expired.'}
              </p>
              <Link to="/forgot-password" className="text-[#E8720C] hover:underline text-sm font-medium">
                {t('auth.requestNewLink') || 'Request a New Link'}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="reset-pw-error">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  {t('auth.newPasswordLabel') || 'New Password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full input-glass pl-12 pr-12"
                    placeholder="Min. 8 characters"
                    required
                    minLength={8}
                    data-testid="reset-pw-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  {t('auth.confirmPasswordLabel') || 'Confirm Password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full input-glass pl-12"
                    placeholder="Repeat password"
                    required
                    minLength={8}
                    data-testid="reset-pw-confirm"
                  />
                </div>
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-red-400 text-xs mt-1">{t('auth.passwordsMustMatch') || 'Passwords do not match'}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="w-full btn-primary py-4 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="reset-pw-submit"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {t('auth.resetting') || 'Resetting...'}</>
                ) : (
                  t('auth.resetPasswordBtn') || 'Reset Password'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
